import { useEffect, useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { SessionState } from "../../shared/types.ts";
import { api, type HealthResponse } from "../lib/api.ts";
import type { RoleAuth } from "../lib/auth.ts";

export type OperatorShellState = Pick<
  SessionState,
  "meta" | "notes" | "settings" | "generation" | "recap" | "stt" | "replay" | "status"
>;

export type RouteSessionArgs =
  | { auth: RoleAuth }
  | { auth: RoleAuth; sessionId: Id<"sessions"> }
  | "skip";

export function emptyRuntimeStatus(): SessionState["status"] {
  return {
    micActive: false,
    micPaused: false,
    captureState: "stopped",
    captureStateChangedAt: null,
    captureClockStartedAt: null,
    captureClockPausedMs: 0,
    captureClockInactiveIntervals: [],
    micLevel: 0,
    selectedInputDeviceId: null,
    selectedInputDeviceLabel: null,
    audioTransport: "disconnected",
    audioTransportError: null,
    sttTransport: "disabled",
    sttError: null,
    lastSttActivityAt: null,
    lastTranscriptAt: null,
    transcriptProjectionVersion: 0,
    lastSuggestionLatencyMs: null,
    connectedClients: 0,
    generating: false,
    generationPhase: null,
    suggestionCheckpointThroughTs: null,
    suggestionCoverage: null,
    liveRecapGenerating: false,
    consolidationGenerating: false,
    recapCoverage: null,
    repairingFromTs: null,
    processedThroughTs: null,
    processingThroughTs: null,
  };
}

/** Polls `GET /api/health` every 15s; returns null until the first response. */
export function useHealthPolling(): HealthResponse | null {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    async function loadHealth() {
      try {
        const next = await api.getHealth();
        if (!cancelled) setHealth(next);
      } catch {
        if (!cancelled) setHealth(null);
      }
    }
    void loadHealth();
    const id = setInterval(loadHealth, 15_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
  return health;
}

/**
 * Merges `sessions.getOperatorShellState` with the live
 * `operatorRuntimeStatus.getForSession` snapshot and polls health.
 */
export function useOperatorState(auth: RoleAuth, routeSessionArgs: RouteSessionArgs) {
  const state = useQuery(convexApi.sessions.getOperatorShellState, routeSessionArgs) as
    | OperatorShellState
    | null
    | undefined;
  const runtimeStatus = useQuery(
    convexApi.operatorRuntimeStatus.getForSession,
    state?.meta.id ? { auth, sessionId: state.meta.id as Id<"sessions"> } : "skip",
  );
  const health = useHealthPolling();

  const viewState = useMemo<OperatorShellState | null | undefined>(() => {
    if (!state) return state;
    return {
      ...state,
      status: {
        ...emptyRuntimeStatus(),
        ...(runtimeStatus ?? {}),
        liveRecapGenerating: state.status.liveRecapGenerating,
        consolidationGenerating: state.status.consolidationGenerating,
        recapCoverage: state.status.recapCoverage ?? null,
        suggestionCoverage: state.status.suggestionCoverage ?? null,
        suggestionCheckpointThroughTs: state.status.suggestionCheckpointThroughTs ?? null,
        lastSuggestionLatencyMs: state.status.suggestionCoverage
          ? state.status.lastSuggestionLatencyMs
          : (runtimeStatus?.lastSuggestionLatencyMs ?? state.status.lastSuggestionLatencyMs),
        repairingFromTs: state.status.repairingFromTs ?? null,
        repairPendingFromTs: state.status.repairPendingFromTs ?? null,
        repairPendingRecapCount: state.status.repairPendingRecapCount ?? 0,
        // `runtimeStatus` (sessionRuntimeStatus doc) has no `consolidation` field, so
        // without this explicit override the spread above leaves it undefined instead
        // of falling back to the shell-computed value from getOperatorShellState.
        consolidation: state.status.consolidation ?? null,
      },
    };
  }, [runtimeStatus, state]);

  return { state, runtimeStatus, viewState, health };
}
